// ============================================================
// ImportExportParts - the pieces the Import and Export dialogs share:
// one checkbox row per section of the session file, and the footer.
//
// A row describes itself as data (`SectionRowSpec`) so each dialog is a
// table of sections rather than a column of near-identical markup.
// ============================================================
import type { ReactNode } from 'react';
import common from '../styles/common.module.css';
import styles from '../styles/ImportExportDialog.module.css';

/** One section of the file, as a dialog lists it. */
export interface SectionRowSpec<K extends string> {
  /** The selection the checkbox toggles. */
  key: K;
  /** The checkbox id (and the label's `htmlFor`). */
  id: string;
  label: ReactNode;
  /** What the section holds: "3 maps", "not in file". */
  count: ReactNode;
  /**
   * Whether the section can be chosen. Undefined means the row is always
   * live and carries no dimming at all; false disables it and dims it.
   */
  available?: boolean;
  testId?: string;
  /** False leaves the row out altogether. */
  show?: boolean;
}

interface SectionRowProps {
  spec: SectionRowSpec<string>;
  checked: boolean;
  onToggle: () => void;
}

function SectionRow({ spec, checked, onToggle }: SectionRowProps) {
  const { id, available } = spec;
  const style = available === undefined ? undefined : { opacity: available ? 1 : 0.4 };
  return (
    <div className={styles.sectionRow}>
      <input
        className={styles.checkbox}
        type="checkbox"
        id={id}
        data-testid={spec.testId}
        checked={checked}
        onChange={onToggle}
        disabled={available === undefined ? undefined : !available}
      />
      <label className={styles.sectionLabel} htmlFor={id} style={style}>
        {spec.label}
      </label>
      <span className={styles.sectionCount} style={style}>
        {spec.count}
      </span>
    </div>
  );
}

/** The dialog's section rows, in table order, skipping any with `show: false`. */
export function SectionRows<K extends string>({
  rows,
  selections,
  onToggle,
}: {
  rows: SectionRowSpec<K>[];
  selections: Record<K, boolean>;
  onToggle: (key: K) => void;
}) {
  return rows
    .filter((row) => row.show !== false)
    .map((row) => (
      <SectionRow key={row.id} spec={row} checked={selections[row.key]} onToggle={() => onToggle(row.key)} />
    ));
}

/** Cancel, and the dialog's verb (disabled when nothing is chosen). */
export function SectionFooter({
  verb,
  onCancel,
  onConfirm,
  disabled,
}: {
  verb: string;
  onCancel: () => void;
  onConfirm: () => void;
  disabled: boolean;
}) {
  return (
    <div className={styles.footer}>
      <button className={common.btnSecondary} onClick={onCancel}>
        Cancel
      </button>
      <button className={common.btnPrimary} onClick={onConfirm} disabled={disabled}>
        {verb}
      </button>
    </div>
  );
}
